import lodashGet from "lodash/get";
import lodashIsEqual from "lodash/isEqual";
import { replace } from "react-router-redux";
import { COLLECTIONS, MERCHANT, MUTATE_UPDATE_SNAPSHOT_FROM_COMMITTED } from "constants/action-types";
import { CollectionKeys } from "constants/product-collection";
import { getAllocationDimensionsBySubtype, getMetricsFromSchema } from "reducers/selectors/collection";
import { AllocationDimensionKind, isAllocationDimensionId } from "helpers/allocation-dimension";
import { parseMetricSchema } from "reducers/dimensions-panel/draft";
import { bootstrapDimensionsPanelState, dimensionsPanelToQueryParams } from "reducers/dimensions-panel/helpers";
import { setDraft } from "actions/tool-panel/draft/actions";
import { setApplied } from "actions/tool-panel/applied/actions";
import { SET_APPLIED } from "actions/tool-panel/applied/constants";
import { DOOR_GROUP_QUERY_KEY, PURCHASE_ORDER_DOOR_GROUP_FIELD } from "helpers/door-collection";
import { SYNC_RECEIVED_UPDATES_WITH_DIMENSION_PANEL_STATE } from "actions/tool-panel/draft/constants";
import { ReceivedUpdatesPayload } from "actions/tool-panel/draft/types";
import { getActiveMerchantPurchaseOrderDoorGroups } from "reducers/selectors/merchant";

// This epic is responsible for managing side-effect for UI related to dimension.
// This includes:
// - Dimension Tool panel
// - We will port existing "door group params" into Redux state. This is a step towards that direction
export const setInitialDimensionViewOnCollectionLoad = (action$, store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.FETCH.COMPLETED)
        .filter(({ error }) => !error)
        .concatMap(({ payload }) => {
            const state = store.getState();
            const genericAllocationDimensions = getAllocationDimensionsBySubtype(
                state,
                payload.collection.id,
                AllocationDimensionKind.Generic
            );
            const metricSchema = lodashGet(
                payload.collection.snapshot.root.mutable_map.fields,
                CollectionKeys.ITEM_DISPLAY_PROPERTY.DOOR_GROUP_METRICS,
                []
            );
            const metrics = parseMetricSchema(metricSchema);
            const queryParams = state.routing.locationBeforeTransitions.query;

            // Take dimensions params from query params
            let dimensionParamsFromUrl = undefined;
            if (queryParams[DOOR_GROUP_QUERY_KEY]) {
                try {
                    dimensionParamsFromUrl = JSON.parse(queryParams[DOOR_GROUP_QUERY_KEY]);
                } catch {
                    // no-op
                }
            }

            const initialDimensionPanelsState = bootstrapDimensionsPanelState({
                queryParams: dimensionParamsFromUrl,
                allocationDimensions: genericAllocationDimensions,
                initialMetrics: metrics,
            });

            // 1. Set draft from initial state
            // 2. Explicitly apply draft
            return [setApplied(initialDimensionPanelsState)];
        });

// When applying dimension panels state, sync it with "draft" for Doors panel
export const syncDraftWithApplied = (action$) =>
    action$.ofType(SET_APPLIED).concatMap(({ payload }) => {
        return [setDraft(payload)];
    });

// When applying dimension panels state, sync it with "draft" for Doors panel
export const syncAppliedWithUrl = (action$, store$) =>
    action$.ofType(SET_APPLIED).concatMap(() => {
        const state = store$.getState();
        const { routing, collection, dimensionsPanel } = state;
        const query = {
            ...routing.locationBeforeTransitions.query,
        };
        const metricsFromSchema = getMetricsFromSchema(state, collection.collectionId);

        const dimensionsQuery = dimensionsPanelToQueryParams(dimensionsPanel.applied, {
            legacyMetrics: metricsFromSchema.length === 0,
        });
        query[DOOR_GROUP_QUERY_KEY] = dimensionsQuery ? JSON.stringify(dimensionsQuery) : undefined;

        // When no URL changes is needed, bail
        if (lodashIsEqual(routing.locationBeforeTransitions.query, query)) {
            return [];
        }

        return [
            replace({
                pathname: routing.locationBeforeTransitions.pathname,
                query,
            }),
        ];
    });

export const syncReceivedUpdatesWithDimensionState = (action$) =>
    action$.ofType(MUTATE_UPDATE_SNAPSHOT_FROM_COMMITTED).concatMap(({ payload }) => {
        const { committedSnapshot, objectId } = payload;

        const isAllocationDimension = isAllocationDimensionId(objectId);
        if (!isAllocationDimension) {
            return [];
        }

        return [
            {
                payload: { data: committedSnapshot.toJS(), dimensionId: objectId } as ReceivedUpdatesPayload,
                type: SYNC_RECEIVED_UPDATES_WITH_DIMENSION_PANEL_STATE,
            },
        ];
    });

/**
 * We need to update PO Door Group view attribute because this attribute is reliant on merchant purchase order.
 */
export const syncPODoorGroupToDimensionState = (action$, store$) =>
    action$
        .ofType(MERCHANT.PURCHASE_ORDER_DOOR_GROUP.LIST.COMPLETE)
        .filter(() => {
            const reduxState = store$.getState();
            const { dimensionsPanel } = reduxState;

            return (
                dimensionsPanel.applied &&
                dimensionsPanel.applied.attributes.some((attribute) => attribute.id === PURCHASE_ORDER_DOOR_GROUP_FIELD)
            );
        })
        .concatMap(() => {
            const reduxState = store$.getState();
            const activePODoorGroups = getActiveMerchantPurchaseOrderDoorGroups(reduxState);
            if (activePODoorGroups.length === 0) {
                return [];
            }

            const { dimensionsPanel } = reduxState;

            const appliedDimensionViewState = dimensionsPanel.applied;
            const mutatedAttributes = appliedDimensionViewState.attributes;
            mutatedAttributes.forEach((attribute) => {
                // In case of Rolloup, there will be more than one `PURCHASE_ORDER_DOOR_GROUP_FIELD`
                if (attribute.id === PURCHASE_ORDER_DOOR_GROUP_FIELD) {
                    // Concat existing options. For this case, it's only Blank option.
                    attribute.values = activePODoorGroups
                        .map((activePODoorGroup) => ({
                            id: activePODoorGroup.id,
                            label: activePODoorGroup.name,
                            // Select children value if attribute is selected
                            selected: attribute.selected,
                        }))
                        .concat(attribute.values ?? []);
                }
            });

            return [
                setApplied({
                    ...appliedDimensionViewState,
                    attributes: mutatedAttributes,
                }),
            ];
        });
